/**
 * Kudeko — ocultación de tamaños por país/mercado.
 *
 * Para Alemania y Francia, oculta cualquier valor de opción cuya medida sea
 * 100x150 cm (también reconoce espacios, el símbolo ×, "cm" y 150x100).
 * La regla afecta a todas las combinaciones de marco y acabado.
 *
 * No muestra avisos ni textos. Si una URL abre directamente una variante
 * restringida, selecciona silenciosamente la medida disponible más próxima
 * conservando el resto de opciones siempre que sea posible.
 */
(function () {
  'use strict';

  var config = window.KDKCountrySizeRestriction || {};
  var activeCountry = normalizeCountry(config.country);
  var restrictedCountries = toArray(config.countries).map(normalizeCountry).filter(Boolean);

  if (!config.enabled || restrictedCountries.indexOf(activeCountry) === -1) {
    return;
  }

  var blockedDimensionKeys = toArray(config.blockedDimensions)
    .map(toDimensionKey)
    .filter(Boolean);

  if (!blockedDimensionKeys.length) {
    return;
  }

  var productDataCache = new WeakMap();
  var scanFrame = null;

  function toArray(value) {
    if (Array.isArray(value)) return value;
    if (value === null || typeof value === 'undefined' || value === '') return [];
    return [value];
  }

  function normalizeCountry(value) {
    return String(value || '').trim().toUpperCase();
  }

  function normalizeDimensionText(value) {
    var text = String(value || '');

    if (typeof text.normalize === 'function') {
      text = text.normalize('NFKC');
    }

    return text
      .toLowerCase()
      .replace(/&times;/g, 'x')
      .replace(/[×✕✖х]/g, 'x')
      .replace(/,/g, '.')
      .trim();
  }

  function dimensionParts(value) {
    var text = normalizeDimensionText(value);
    var match = text.match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*x\s*(\d+(?:\.\d+)?)\s*(?:cm)?/i);

    if (!match) return null;

    var first = Number(match[1]);
    var second = Number(match[2]);

    if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) {
      return null;
    }

    return first <= second ? [first, second] : [second, first];
  }

  function formatDimensionNumber(value) {
    return String(Math.round(value * 1000) / 1000);
  }

  function toDimensionKey(value) {
    var parts = dimensionParts(value);
    if (!parts) return '';
    return formatDimensionNumber(parts[0]) + 'x' + formatDimensionNumber(parts[1]);
  }

  function isBlockedDimension(value) {
    var key = toDimensionKey(value);
    return key !== '' && blockedDimensionKeys.indexOf(key) !== -1;
  }

  function getVariantOptions(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options.slice();

    return [variant.option1, variant.option2, variant.option3].filter(function (value) {
      return value !== null && typeof value !== 'undefined';
    });
  }

  function isBlockedVariant(variant) {
    return getVariantOptions(variant).some(isBlockedDimension);
  }

  function getProductData(form) {
    if (productDataCache.has(form)) {
      return productDataCache.get(form);
    }

    var jsonScript = form.querySelector('script[data-product-json]');
    var parsed = null;

    if (jsonScript) {
      try {
        parsed = JSON.parse(jsonScript.textContent || jsonScript.innerHTML || '{}');
      } catch (error) {
        parsed = null;
      }
    }

    if (!parsed || !parsed.product || !Array.isArray(parsed.product.variants)) {
      return null;
    }

    productDataCache.set(form, parsed);
    return parsed;
  }

  function getMasterVariantControl(form) {
    return form.querySelector('select[name="id"]') || form.querySelector('input[name="id"]');
  }

  function findVariantById(productData, id) {
    if (!productData || !productData.product || id === null || typeof id === 'undefined') {
      return null;
    }

    var normalizedId = String(id);
    var variants = productData.product.variants;

    for (var index = 0; index < variants.length; index += 1) {
      if (String(variants[index].id) === normalizedId) {
        return variants[index];
      }
    }

    return null;
  }

  function getCurrentVariant(form, productData) {
    if (!productData) return null;

    var masterControl = getMasterVariantControl(form);
    var variant = masterControl ? findVariantById(productData, masterControl.value) : null;

    if (variant) return variant;

    var selectedValues = {};
    var checkedInputs = form.querySelectorAll('input[type="radio"][data-option-position]:checked');

    for (var inputIndex = 0; inputIndex < checkedInputs.length; inputIndex += 1) {
      selectedValues[String(checkedInputs[inputIndex].getAttribute('data-option-position'))] = checkedInputs[inputIndex].value;
    }

    var variants = productData.product.variants;

    for (var variantIndex = 0; variantIndex < variants.length; variantIndex += 1) {
      var options = getVariantOptions(variants[variantIndex]);
      var matches = true;

      for (var optionIndex = 0; optionIndex < options.length; optionIndex += 1) {
        var selectedValue = selectedValues[String(optionIndex + 1)];

        if (typeof selectedValue !== 'undefined' && String(options[optionIndex]) !== String(selectedValue)) {
          matches = false;
          break;
        }
      }

      if (matches && Object.keys(selectedValues).length) {
        return variants[variantIndex];
      }
    }

    return findVariantById(productData, productData.selected_variant_id);
  }

  function getControlValue(control) {
    if (!control) return '';
    if (typeof control.value !== 'undefined' && control.value !== '') return control.value;
    if (control.hasAttribute('data-value')) return control.getAttribute('data-value');
    if (control.hasAttribute('data-option-value')) return control.getAttribute('data-option-value');
    return '';
  }

  function hideElement(element) {
    if (!element || element.getAttribute('data-kdk-country-size-hidden') === 'true') return;

    element.classList.add('KdkCountrySizeRestriction__Hidden');
    element.setAttribute('data-kdk-country-size-hidden', 'true');
    element.setAttribute('aria-hidden', 'true');
  }

  function disableControl(control) {
    if (!control) return;

    control.setAttribute('data-kdk-country-restricted', 'true');
    control.setAttribute('aria-disabled', 'true');

    if (control.matches('input, button, select, option')) {
      control.disabled = true;
    }
  }

  function hideControlAndRelatedElements(control) {
    if (!control) return;

    disableControl(control);

    if (control.matches('input[type="radio"]')) {
      var wrapper = control.closest('.HorizontalList__Item, .VariantSelector__Item, .VariantSelector__Choice');

      if (wrapper) {
        hideElement(wrapper);
      } else {
        hideElement(control);

        var labels = control.labels ? Array.prototype.slice.call(control.labels) : [];
        labels.forEach(hideElement);
      }

      return;
    }

    if (control.tagName === 'OPTION') {
      control.hidden = true;
      return;
    }

    var relatedWrapper = control.closest('.HorizontalList__Item, .VariantSelector__Item, .VariantSelector__Choice');
    hideElement(relatedWrapper || control);
  }

  function hideRestrictedControls(form, productData) {
    var controls = form.querySelectorAll(
      'input[type="radio"][data-option-position], ' +
      'button[data-option-position][data-value], ' +
      '[data-option-position][data-option-value]'
    );
    var found = false;

    for (var controlIndex = 0; controlIndex < controls.length; controlIndex += 1) {
      var control = controls[controlIndex];
      if (!isBlockedDimension(getControlValue(control))) continue;

      found = true;
      hideControlAndRelatedElements(control);
    }

    if (productData) {
      var masterControl = getMasterVariantControl(form);

      if (masterControl && masterControl.tagName === 'SELECT') {
        var options = masterControl.options;

        for (var optionIndex = 0; optionIndex < options.length; optionIndex += 1) {
          var variant = findVariantById(productData, options[optionIndex].value);

          if (variant && isBlockedVariant(variant)) {
            found = true;
            disableControl(options[optionIndex]);
            options[optionIndex].hidden = true;
          }
        }
      }
    }

    return found;
  }

  function findBlockedOptionIndex(variant) {
    var options = getVariantOptions(variant);

    for (var index = 0; index < options.length; index += 1) {
      if (isBlockedDimension(options[index])) return index;
    }

    return -1;
  }

  function candidateDistance(targetValue, candidateValue) {
    var target = dimensionParts(targetValue);
    var candidate = dimensionParts(candidateValue);

    if (!target || !candidate) return Number.MAX_SAFE_INTEGER;

    var edgeDifference = Math.abs(target[0] - candidate[0]) + Math.abs(target[1] - candidate[1]);
    var targetArea = target[0] * target[1];
    var candidateArea = candidate[0] * candidate[1];
    var largerPenalty = candidateArea > targetArea ? 1000000 : 0;
    var areaDifference = Math.abs(targetArea - candidateArea) / 1000;

    return largerPenalty + edgeDifference * 1000 + areaDifference;
  }

  function findFallbackVariant(productData, currentVariant, blockedOptionIndex) {
    var currentOptions = getVariantOptions(currentVariant);
    var variants = productData.product.variants;
    var candidates = [];

    for (var variantIndex = 0; variantIndex < variants.length; variantIndex += 1) {
      var candidate = variants[variantIndex];
      var candidateOptions = getVariantOptions(candidate);

      if (!candidate.available || isBlockedVariant(candidate) || candidateOptions.length !== currentOptions.length) {
        continue;
      }

      var sameOtherOptions = true;

      for (var optionIndex = 0; optionIndex < currentOptions.length; optionIndex += 1) {
        if (optionIndex === blockedOptionIndex) continue;

        if (String(candidateOptions[optionIndex]) !== String(currentOptions[optionIndex])) {
          sameOtherOptions = false;
          break;
        }
      }

      if (!sameOtherOptions) continue;

      candidates.push({
        variant: candidate,
        distance: candidateDistance(currentOptions[blockedOptionIndex], candidateOptions[blockedOptionIndex])
      });
    }

    candidates.sort(function (first, second) {
      if (first.distance !== second.distance) return first.distance - second.distance;
      return Number(second.variant.id) - Number(first.variant.id);
    });

    return candidates.length ? candidates[0].variant : null;
  }

  function valuesMatch(first, second) {
    return String(first || '').trim() === String(second || '').trim();
  }

  function findOptionControl(form, optionPosition, value) {
    var radios = form.querySelectorAll('input[type="radio"][data-option-position="' + optionPosition + '"]');
    var index;

    for (index = 0; index < radios.length; index += 1) {
      if (valuesMatch(radios[index].value, value)) return radios[index];
    }

    var popoverButtons = form.querySelectorAll('button[data-option-position="' + optionPosition + '"][data-value]');

    for (index = 0; index < popoverButtons.length; index += 1) {
      if (valuesMatch(popoverButtons[index].getAttribute('data-value'), value)) return popoverButtons[index];
    }

    var genericControls = form.querySelectorAll('[data-option-position="' + optionPosition + '"][data-option-value]');

    for (index = 0; index < genericControls.length; index += 1) {
      if (valuesMatch(genericControls[index].getAttribute('data-option-value'), value)) return genericControls[index];
    }

    return null;
  }

  function synchronizeMasterVariant(form, variantId) {
    var masterControl = getMasterVariantControl(form);
    if (!masterControl) return;

    if (masterControl.tagName === 'SELECT') {
      var options = masterControl.options;

      for (var index = 0; index < options.length; index += 1) {
        var shouldSelect = String(options[index].value) === String(variantId);
        options[index].selected = shouldSelect;

        if (shouldSelect) {
          options[index].setAttribute('selected', 'selected');
        } else {
          options[index].removeAttribute('selected');
        }
      }
    }

    masterControl.value = String(variantId);
  }

  function activateControl(control) {
    if (!control) return false;

    if (typeof control.click === 'function') {
      control.click();
      return true;
    }

    control.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    return true;
  }

  function maybeSelectFallback(form, productData) {
    if (config.autoSelectFallback === false || !productData) return false;

    var currentVariant = getCurrentVariant(form, productData);

    if (!currentVariant || !isBlockedVariant(currentVariant)) {
      form.removeAttribute('data-kdk-fallback-attempted');
      return false;
    }

    if (form.getAttribute('data-kdk-fallback-attempted') === String(currentVariant.id)) {
      return false;
    }

    var blockedOptionIndex = findBlockedOptionIndex(currentVariant);
    if (blockedOptionIndex < 0) return false;

    var fallbackVariant = findFallbackVariant(productData, currentVariant, blockedOptionIndex);
    if (!fallbackVariant) return false;

    var fallbackOptions = getVariantOptions(fallbackVariant);
    var targetValue = fallbackOptions[blockedOptionIndex];
    var targetControl = findOptionControl(form, String(blockedOptionIndex + 1), targetValue);

    if (!targetControl || targetControl.disabled || isBlockedDimension(getControlValue(targetControl))) {
      return false;
    }

    form.setAttribute('data-kdk-fallback-attempted', String(currentVariant.id));
    synchronizeMasterVariant(form, fallbackVariant.id);
    activateControl(targetControl);
    queueScan();

    return true;
  }

  function selectedRestrictedControlExists(form) {
    return Boolean(
      form.querySelector('input[data-kdk-country-restricted="true"]:checked') ||
      form.querySelector('.Popover__Value.is-selected[data-kdk-country-restricted="true"]') ||
      form.querySelector('.VariantSelector__Item.is-selected[data-kdk-country-restricted="true"]')
    );
  }

  function rememberButtonState(button) {
    if (button.hasAttribute('data-kdk-country-button-guard')) return;

    button.setAttribute('data-kdk-country-button-guard', 'true');
    button.setAttribute('data-kdk-country-was-disabled', button.disabled ? 'true' : 'false');
    button.setAttribute(
      'data-kdk-country-previous-action',
      button.hasAttribute('data-action') ? button.getAttribute('data-action') : '__none__'
    );
    button.setAttribute(
      'data-kdk-country-previous-aria-disabled',
      button.hasAttribute('aria-disabled') ? button.getAttribute('aria-disabled') : '__none__'
    );
  }

  function blockButton(button) {
    rememberButtonState(button);
    button.disabled = true;
    button.setAttribute('disabled', 'disabled');
    button.setAttribute('aria-disabled', 'true');
    button.removeAttribute('data-action');
  }

  function restoreButton(button) {
    if (!button.hasAttribute('data-kdk-country-button-guard')) return;

    var wasDisabled = button.getAttribute('data-kdk-country-was-disabled') === 'true';
    var previousAction = button.getAttribute('data-kdk-country-previous-action');
    var previousAriaDisabled = button.getAttribute('data-kdk-country-previous-aria-disabled');

    if (wasDisabled) {
      button.disabled = true;
      button.setAttribute('disabled', 'disabled');
    } else {
      button.disabled = false;
      button.removeAttribute('disabled');
    }

    if (previousAction === '__none__') {
      button.removeAttribute('data-action');
    } else {
      button.setAttribute('data-action', previousAction);
    }

    if (previousAriaDisabled === '__none__') {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', previousAriaDisabled);
    }

    button.removeAttribute('data-kdk-country-button-guard');
    button.removeAttribute('data-kdk-country-was-disabled');
    button.removeAttribute('data-kdk-country-previous-action');
    button.removeAttribute('data-kdk-country-previous-aria-disabled');
  }

  function applyProductPurchaseGuard(form, productData) {
    var currentVariant = getCurrentVariant(form, productData);
    var blocked = currentVariant ? isBlockedVariant(currentVariant) : selectedRestrictedControlExists(form);
    var addToCartButtons = form.querySelectorAll('.ProductForm__AddToCart, [data-action="add-to-cart"]');

    form.classList.toggle('KdkCountrySizeRestriction--blocked', blocked);
    form.setAttribute('data-kdk-current-variant-restricted', blocked ? 'true' : 'false');

    for (var buttonIndex = 0; buttonIndex < addToCartButtons.length; buttonIndex += 1) {
      if (blocked) {
        blockButton(addToCartButtons[buttonIndex]);
      } else {
        restoreButton(addToCartButtons[buttonIndex]);
      }
    }

    return blocked;
  }

  function processProductForm(form) {
    if (!form || !document.documentElement.contains(form)) return;

    var productData = getProductData(form);
    var foundRestrictedControl = hideRestrictedControls(form, productData);
    var hasBlockedVariant = Boolean(productData && productData.product.variants.some(isBlockedVariant));

    if (!foundRestrictedControl && !hasBlockedVariant) return;

    maybeSelectFallback(form, productData);
    applyProductPurchaseGuard(form, productData);
  }

  function getProductForms(root) {
    var scope = root || document;
    var forms = [];

    if (scope.matches && scope.matches('form[action*="/cart/add"]')) {
      forms.push(scope);
    }

    if (scope.querySelectorAll) {
      var foundForms = scope.querySelectorAll('form[action*="/cart/add"]');

      for (var index = 0; index < foundForms.length; index += 1) {
        if (forms.indexOf(foundForms[index]) === -1) forms.push(foundForms[index]);
      }
    }

    return forms;
  }

  function processAllProductForms(root) {
    var forms = getProductForms(root || document);

    for (var index = 0; index < forms.length; index += 1) {
      processProductForm(forms[index]);
    }
  }

  function queueScan() {
    if (scanFrame !== null) return;

    scanFrame = window.requestAnimationFrame(function () {
      scanFrame = null;
      processAllProductForms(document);
    });
  }

  function preventBlockedPurchase(event, form) {
    if (!form || form.getAttribute('data-kdk-current-variant-restricted') !== 'true') return false;

    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    return true;
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target.closest(
      '.ProductForm__AddToCart, [data-action="add-to-cart"], .shopify-payment-button'
    ) : null;

    if (!target) return;

    var form = target.closest('form[action*="/cart/add"]');
    preventBlockedPurchase(event, form);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (form.matches && form.matches('form[action*="/cart/add"]')) {
      preventBlockedPurchase(event, form);
    }
  }, true);

  document.addEventListener('change', function (event) {
    if (event.target && event.target.closest && event.target.closest('form[action*="/cart/add"]')) {
      queueScan();
    }
  });

  document.addEventListener('variant:changed', queueScan);
  document.addEventListener('shopify:section:load', function (event) {
    processAllProductForms(event.target || document);
  });

  window.addEventListener('pageshow', queueScan);

  function initialize() {
    processAllProductForms(document);

    var observer = new MutationObserver(function (mutations) {
      var shouldScan = false;

      for (var mutationIndex = 0; mutationIndex < mutations.length; mutationIndex += 1) {
        var addedNodes = mutations[mutationIndex].addedNodes;

        for (var nodeIndex = 0; nodeIndex < addedNodes.length; nodeIndex += 1) {
          var node = addedNodes[nodeIndex];
          if (!node || node.nodeType !== 1) continue;

          if ((node.matches && node.matches('form[action*="/cart/add"], [data-option-position]')) ||
              (node.querySelector && node.querySelector('form[action*="/cart/add"], [data-option-position]'))) {
            shouldScan = true;
            break;
          }
        }

        if (shouldScan) break;
      }

      if (shouldScan) queueScan();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  window.KDKCountrySizeRestrictionAPI = {
    refresh: function () {
      processAllProductForms(document);
    },
    isBlockedDimension: isBlockedDimension,
    getState: function () {
      return {
        country: activeCountry,
        blockedDimensions: blockedDimensionKeys.slice()
      };
    }
  };
}());
